import { useEffect, useState } from "react";
import packageJson from "../../../package.json";
import pdf from "../../assets/pdf/Placholder_pdf.pdf";
import { useAppContext } from "../../classes/contexts/AppContext";
import { SupportCategoriesEnum } from "../../classes/enums/SupportEnum";
import useTranslation from "../../hooks/i18n/useTranslation";
import {
  fetchDocsFileBlob,
  openDocsResolvedPdfInNewTab,
  resolveEifu,
  resolveSla,
} from "../../services/api/Docs";
import { getToken } from "../../classes/utils/TokenUtils";
import { isDocsEnabled, getEifuVersion } from "../../config/docsConfig";
import { Contact } from "./components/contact/Contact";
import "./Support.css";

const SupportCategory = ({ category, isSelected, onClick }) => {
  return (
    <div
      className={`support-category ${isSelected ? "selected" : ""}`}
      onClick={onClick}
    >
      <span className="App-text-medium">{category}</span>
    </div>
  );
};

const VideoGallery = () => {
  const { t } = useTranslation();
  const videos = [
    {
      id: 1,
      title: t("327"), // Question
      embedUrl: "https://www.youtube.com/embed/Sagg08DrO5U",
    },
    {
      id: 2,
      title: t("328"), // Feedback
      embedUrl: "https://www.youtube.com/embed/Sagg08DrO5U",
    },
    {
      id: 3,
      title: t("329"), // Bug Report
      embedUrl: "https://www.youtube.com/embed/Sagg08DrO5U",
    },
    {
      id: 4,
      title: t("330"), // Feature Request
      embedUrl: "https://www.youtube.com/embed/Sagg08DrO5U",
    },
    {
      id: 5,
      title: t("300"), // Contact Support
      embedUrl: "https://www.youtube.com/embed/Sagg08DrO5U",
    },
    {
      id: 6,
      title: t("342"), // Contact Us
      embedUrl: "https://www.youtube.com/embed/Sagg08DrO5U",
    },
    {
      id: 7,
      title: t("26"), // Support
      embedUrl: "https://www.youtube.com/embed/Sagg08DrO5U",
    },
  ];

  return (
    <div className="support-content-inner">
      <div className="support-section-header">
        <h1 className="support-section-title">{t("26")}</h1>
        <p className="support-section-description">{t("343")}</p>
      </div>
      <div className="video-gallery">
        {videos.map(video => (
          <div key={video.id} className="video-item">
            <div className="video-info">
              <div className="App-text-xlarge App-bold-text">{video.title}</div>
              <div className="App-text-medium">{t("343")}</div>
            </div>
            <iframe
              width="100%"
              height="100%"
              src={video.embedUrl}
              title={video.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              style={{
                borderRadius: "10px",
              }}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

const DocumentationReader = () => {
  const { t } = useTranslation();
  const docsEnabled = isDocsEnabled();
  const [pdfSrc, setPdfSrc] = useState(docsEnabled ? null : pdf);

  useEffect(() => {
    if (!docsEnabled) return;
    let objectUrl = null;
    let cancelled = false;

    (async () => {
      const version = getEifuVersion() || "";
      const { files, error } = await resolveEifu(version);
      if (cancelled) return;
      const firstUrl = files?.[0]?.url;
      if (error || !firstUrl) {
        setPdfSrc(pdf);
        return;
      }
      const token = getToken();
      if (!token) {
        setPdfSrc(pdf);
        return;
      }
      try {
        const blob = await fetchDocsFileBlob(firstUrl);
        const nextUrl = URL.createObjectURL(blob);
        if (cancelled) {
          URL.revokeObjectURL(nextUrl);
          return;
        }
        objectUrl = nextUrl;
        setPdfSrc(objectUrl);
      } catch {
        if (!cancelled) setPdfSrc(pdf);
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [docsEnabled]);

  return (
    <div className="support-content-inner">
      <div className="support-section-header">
        <h1 className="support-section-title">Documentation</h1>
        <p className="support-section-description">
          {t("465")}
          <br />
          {t("466")}
        </p>
      </div>
      <div className="pdf-viewer">
        {pdfSrc && (
          <iframe
            src={pdfSrc}
            width="100%"
            height="100%"
            style={{
              border: "none",
              borderRadius: "15px",
            }}
            allowFullScreen
            title="PDF Viewer"
          />
        )}
      </div>
    </div>
  );
};

const Support = () => {
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState(
    SupportCategoriesEnum.CONTACTS
  );

  const { hideSidebars } = useAppContext();

  useEffect(() => {
    hideSidebars();
  }, [hideSidebars]);

  const supportCategories = [
    {
      category: SupportCategoriesEnum.CONTACTS,
    },
    {
      category: SupportCategoriesEnum.DOCUMENTATION,
    },
  ];

  const renderContent = () => {
    switch (selectedCategory) {
      case SupportCategoriesEnum.CONTACTS:
        return <Contact />;
      case SupportCategoriesEnum.DOCUMENTATION:
        return <DocumentationReader />;
      default:
        return <Contact />;
    }
  };

  return (
    <div className="support-container">
      <div className="support-categories-container">
        <div className="support-categories-section">
          {supportCategories.map((category, index) => (
            <SupportCategory
              key={index}
              category={category.category}
              isSelected={selectedCategory === category.category}
              onClick={() => setSelectedCategory(category.category)}
            />
          ))}
        </div>
      </div>
      <div className="support-content-container">{renderContent()}</div>
      <div className="support-footer">
        <div className="support-footer-links">
          <button
            type="button"
            className="App-text-small footer-link"
            onClick={() => openDocsResolvedPdfInNewTab(resolveSla)}
          >
            Software License Agreement (SLA)
          </button>
        </div>
        <span className="App-text-small">All rights reserved</span>
        <div className="support-footer-version">
          <span className="App-text-small">{packageJson?.version}</span>
        </div>
      </div>
    </div>
  );
};

export default Support;
